import React, { useMemo, useState } from 'react'

import Modal from 'react-modal'

import SearchIcon from '@src/components/icons/Search'
import { supportedFiatCurrencies } from '@src/pages/FiatExchange/config'
// or pass in as props if needed
import { FiatCurrency } from '@src/types/fiat'

interface SelectFiatModalProps {
    /** Controls visibility of the modal */
    isOpen: boolean
    /** Callback to close the modal */
    onClose: () => void
    /** The currently selected fiat (if any) */
    selectedFiat?: FiatCurrency
    /** Called when the user picks a fiat currency */
    setSelectedFiat: (fiat: FiatCurrency) => void
    /**
     * Optionally override the list of fiat currencies
     * If you want to pass from outside, do so. Otherwise you can rely on your `supportedFiatCurrencies`.
     */
    allowedFiats?: FiatCurrency[]
}

function FiatResultsDisplay({
    fiats,
    onFiatClicked,
    selectedFiat,
}: {
    fiats: FiatCurrency[]
    onFiatClicked: (fiat: FiatCurrency) => void
    selectedFiat?: FiatCurrency
}) {
    if (fiats.length === 0) {
        return <p className='mt-4 text-center text-sm text-gray-400'>No fiat currencies found.</p>
    }

    return (
        <div className='flex flex-col gap-2'>
            {fiats.map(fiat => {
                const isSelected = fiat.symbol === selectedFiat?.symbol
                return (
                    <button
                        key={fiat.symbol}
                        onClick={() => onFiatClicked(fiat)}
                        className={`flex items-center gap-3 rounded-md px-4 py-3.5 transition-colors duration-200 hover:bg-token-modal-chain-hover ${isSelected ? 'border border-token-modal-border bg-token-modal-chain-selected' : ''} `}
                    >
                        <img
                            src={fiat.logoURI}
                            alt={fiat.symbol}
                            onError={e => {
                                e.currentTarget.src = '/assets/token/unknown.png'
                            }}
                            className='h-5 w-5 rounded-full object-cover'
                        />
                        <div className='flex flex-col items-start'>
                            <span className='text-[14px] font-bold'>{fiat.symbol}</span>
                            <span className='text-xs text-token-modal-label-text'>{fiat.name}</span>
                        </div>
                    </button>
                )
            })}
        </div>
    )
}

export default function SelectFiatModal({
    isOpen,
    onClose,
    selectedFiat,
    setSelectedFiat,
    allowedFiats = supportedFiatCurrencies, // fallback to all supported
}: SelectFiatModalProps): JSX.Element {
    const [fiatQuery, setFiatQuery] = useState('')

    // Modal styles – mirroring the token modal styles
    const modalStyle = {
        overlay: {
            position: 'fixed' as const,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'var(--token-modal-overlay)',
            zIndex: 50,
            padding: '16px',
        },
        content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            transform: 'translate(-50%, -50%)',
            width: '95%',
            maxWidth: '576px',
            height: '680px',
            padding: 0,
            border: '1px solid var(--token-modal-border)',
            borderRadius: '0.375rem',
            background: 'var(--token-modal-background)',
            overflow: 'hidden',
        },
    }

    // Filter the fiat currencies by name, symbol
    const filteredFiats = useMemo(() => {
        const q = fiatQuery.trim().toLowerCase()
        if (!q) return allowedFiats

        return allowedFiats.filter(fiat => {
            const inName = fiat.name.toLowerCase().includes(q)
            const inSymbol = fiat.symbol.toLowerCase().includes(q)
            return inName || inSymbol
        })
    }, [fiatQuery, allowedFiats])

    const handleFiatSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFiatQuery(e.target.value)
    }

    const handleFiatClick = (fiat: FiatCurrency) => {
        setSelectedFiat(fiat)
        onClose()
    }

    return (
        <Modal isOpen={isOpen} onRequestClose={onClose} style={modalStyle} ariaHideApp={false}>
            {/* MOBILE VIEW */}
            <div className='flex h-full flex-col sm:hidden'>
                {/* Header */}
                <div className='flex items-center justify-between p-4'>
                    <h2 className='text-[20px] font-semibold'>Select Fiat</h2>
                    <button
                        onClick={onClose}
                        className='text-white/70 transition-colors hover:text-white'
                        type='button'
                    >
                        <svg width='24' height='24' fill='none' viewBox='0 0 24 24'>
                            <path
                                d='M18 6L6 18M6 6L18 18'
                                stroke='currentColor'
                                strokeWidth='2'
                                strokeLinecap='round'
                            />
                        </svg>
                    </button>
                </div>

                {/* Search */}
                <div className='px-4 pb-4'>
                    <div className='flex w-full items-center gap-2 rounded-md border border-token-modal-border bg-token-modal-input-bg px-3 py-2.5'>
                        <SearchIcon className='h-5 w-5 text-token-modal-input-placeholder' />
                        <input
                            type='text'
                            placeholder='Search by name or symbol'
                            value={fiatQuery}
                            onChange={handleFiatSearchChange}
                            className='w-full bg-transparent text-white placeholder-token-modal-input-placeholder outline-none'
                        />
                    </div>
                </div>

                {/* Fiat list */}
                <div className='flex-1 overflow-y-auto px-4'>
                    <FiatResultsDisplay
                        fiats={filteredFiats}
                        onFiatClicked={handleFiatClick}
                        selectedFiat={selectedFiat}
                    />
                </div>
            </div>

            {/* DESKTOP VIEW */}
            <div className='hidden h-full flex-col sm:flex'>
                <div className='flex items-center justify-between p-4'>
                    <h2 className='text-[20px] font-semibold'>Select Fiat</h2>
                    <button
                        onClick={onClose}
                        className='text-white/70 transition-colors hover:text-white'
                        type='button'
                    >
                        <svg width='24' height='24' fill='none' viewBox='0 0 24 24'>
                            <path
                                d='M18 6L6 18M6 6L18 18'
                                stroke='currentColor'
                                strokeWidth='2'
                                strokeLinecap='round'
                            />
                        </svg>
                    </button>
                </div>

                {/* Search Bar */}
                <div className='px-4 pt-0'>
                    <div className='flex w-full items-center gap-2 rounded-md border border-token-modal-border bg-token-modal-input-bg px-3 py-2.5'>
                        <SearchIcon className='h-5 w-5 text-token-modal-input-placeholder' />
                        <input
                            type='text'
                            placeholder='Search by name or symbol'
                            value={fiatQuery}
                            onChange={handleFiatSearchChange}
                            className='w-full bg-transparent text-white placeholder-token-modal-input-placeholder outline-none'
                        />
                    </div>
                </div>

                {/* Fiat list (scrollable) */}
                <div className='flex-1 overflow-y-auto px-4 pb-4 pt-4'>
                    <FiatResultsDisplay
                        fiats={filteredFiats}
                        onFiatClicked={handleFiatClick}
                        selectedFiat={selectedFiat}
                    />
                </div>
            </div>
        </Modal>
    )
}
